import { QUERY_STATUS, SCOPES } from "constants/constants";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
} from "react";

import {
  getFileByIdQuery,
  createFileQuery,
  uploadProfilePictureQuery,
  deleteFileQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";

const fileContext = createContext({});

export const useFileContext = () => useContext(fileContext);

export const FileProvider = ({ ...props }) => {
  const [fileById, setFileById] = useState("");
  const [createdFile, setCreatedFile] = useState({});
  const [createFileStatus, setCreateFileStatus] = useState();
  const [loadStatus, setLoadStatus] = useState(QUERY_STATUS.INITIAL);
  const currentRequestIdRef = useRef(null);
  const { userScope } = useUserContext();

  const deleteFile = useCallback(async ({ userId }) => {
    try {
      return await deleteFileQuery({ userId, userScope });
    } catch (e) {
      console.error("deleteFile", e);
      throw e;
    }
  }, []);

  const createFile = useCallback(async (data) => {
    try {
      const result = await createFileQuery(data);
      setCreatedFile(result);
      setCreateFileStatus(QUERY_STATUS.SUCCESS);
    } catch (e) {
      console.error("createFile", e);
      setCreateFileStatus(QUERY_STATUS.ERROR);
      throw e;
    }
  }, []);

  const uploadProfilePicture = useCallback(
    async ({ userId, file, scope = SCOPES.ADMIN }) => {
      try {
        const result = await uploadProfilePictureQuery({ userId, file, scope });
        setCreatedFile(result);
        setCreateFileStatus(QUERY_STATUS.SUCCESS);
        return result;
      } catch (e) {
        console.error("uploadProfilePicture", e);
        setCreateFileStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    []
  );

  const getFileById = useCallback(async ({ id, isBase64Request }) => {
    if (id?.length > 0) {
      const requestId = Date.now();
      currentRequestIdRef.current = requestId;
      setLoadStatus(QUERY_STATUS.LOADING);

      try {
        const result = await getFileByIdQuery({ id, isBase64Request });

        if (currentRequestIdRef.current === requestId) {
          setFileById(result);
          setLoadStatus(QUERY_STATUS.SUCCESS);
        }
      } catch (error) {
        if (currentRequestIdRef.current === requestId) {
          console.error("Error in getFileById:", error);
          setLoadStatus(QUERY_STATUS.ERROR);
        }
      }
    }
  }, []);

  const values = useMemo(
    () => ({
      fileById,
      setFileById,
      createdFile,
      setCreatedFile,
      createFileStatus,
      setCreateFileStatus,
      createFile,
      deleteFile,
      uploadProfilePicture,
      getFileById,
      loadStatus,
      setLoadStatus,
    }),
    [fileById, createdFile, createFileStatus, loadStatus]
  );
  return <fileContext.Provider value={values} {...props} />;
};
